import React, { useMemo, useState } from "react";
import { Plus, Check, Trash2, Search } from "react-feather";
import Select from "react-select";
import axios from "axios";
import { message } from "antd";

const getCurrency = () => localStorage.getItem("currencycode") || "₹";

const money = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

const getMenuId = (item) => item?.menu_id || item?.item_id || item?.id;

const normalizeItemForAdd = (item) => {
  const menuId = getMenuId(item);

  return {
    ...item,
    menu_id: menuId,
    price_standard: Number(item?.price_standard || item?.price || item?.rate || 0),
  };
};

const getBillQty = (billItems = [], menuId) => {
  const found = billItems.find((billItem) => String(billItem.menu_id) === String(menuId));
  return Number(found?.qty || 0);
};

const getItemCategory = (item) =>
  item?.category_name || item?.category || item?.group_name || item?.menu_category || "Menu";

const getOneLineName = (value) => String(value || "Unnamed Item").trim();

const unitOptions = [
  { value: "NOS", label: "NOS" },
  { value: "CUP", label: "CUP" },
  { value: "KG", label: "KG" },
  { value: "LITER", label: "LITER" },
  { value: "PLATE", label: "PLATE" },
  { value: "GRAM", label: "GRAM" },
  { value: "ML", label: "ML" },
];

const unitSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 28,
    height: 28,
    borderRadius: 10,
    borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.12)",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(37, 99, 235, 0.12)" : "none",
    background: "#ffffff",
    cursor: "pointer",
    "&:hover": {
      borderColor: state.isFocused ? "#2563eb" : "rgba(15, 23, 42, 0.22)",
    },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 7px", height: 28 }),
  indicatorsContainer: (base) => ({ ...base, height: 28 }),
  dropdownIndicator: (base) => ({ ...base, padding: 3 }),
  clearIndicator: (base) => ({ ...base, padding: 3 }),
  menuPortal: (base) => ({ ...base, zIndex: 2147483001 }),
  menu: (base) => ({
    ...base,
    zIndex: 2147483001,
    borderRadius: 12,
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 12,
    fontWeight: state.isSelected ? 850 : 650,
    cursor: "pointer",
  }),
  singleValue: (base) => ({
    ...base,
    color: "#334155",
    fontSize: 11,
    fontWeight: 900,
  }),
  placeholder: (base) => ({
    ...base,
    color: "#94a3b8",
    fontSize: 11,
    fontWeight: 700,
  }),
};

const MenuItemGrid = ({
  filteredItems = [],
  billItems = [],
  handleAddItem,
  settings,
  apiUrl,
  token,
}) => {
  const [unitSelections, setUnitSelections] = useState({});
  const [updatingUnitId, setUpdatingUnitId] = useState(null);

  //const enableUnits =  !!settings?.menu_settings?.enable_units;
  const currency = getCurrency();
const enableUnits = false; // force off
  const visibleItems = useMemo(
    () => (Array.isArray(filteredItems) ? filteredItems : []),
    [filteredItems]
  );

  const handleUnitChange = async (menuId, unit) => {
    if (!menuId) {
      message.error("Unable to update unit. Menu item id is missing.");
      return;
    }

    const selectedUnit = unit || null;

    setUnitSelections((prev) => ({ ...prev, [menuId]: selectedUnit }));
    setUpdatingUnitId(menuId);

    try {
      await axios.put(
        `${apiUrl}/pos/items/update`,
        { menu_id: menuId, unit: selectedUnit ? selectedUnit.value : null },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      message.success(selectedUnit ? "Unit updated successfully." : "Unit removed successfully.");
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to update unit");
    } finally {
      setUpdatingUnitId(null);
    }
  };

  const addItem = (item) => {
    const normalized = normalizeItemForAdd(item);

    if (!normalized.menu_id) {
      message.error("Unable to add item. Menu item id is missing.");
      return;
    }

    if (typeof handleAddItem === "function") {
      handleAddItem(normalized);
    }
  };

  return (
    <div className="ns-menu-grid-wrap">
      <style>{`
        .ns-menu-grid-wrap {
          width: 100%;
          height: 100%;
          min-height: 0;
        }

        .ns-menu-grid {
          height: 100%;
          min-height: 360px;
          overflow-y: auto;
          overflow-x: hidden;
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(152px, 1fr));
          grid-auto-rows: ${enableUnits ? "126px" : "86px"};
          align-items: stretch;
          gap: 9px;
          padding: 2px 2px 110px;
          scrollbar-width: thin;
          scrollbar-color: #93c5fd #e2e8f0;
        }

        .ns-menu-grid::-webkit-scrollbar {
          width: 8px;
        }

        .ns-menu-grid::-webkit-scrollbar-track {
          background: #e2e8f0;
          border-radius: 999px;
        }

        .ns-menu-grid::-webkit-scrollbar-thumb {
          background: #93c5fd;
          border-radius: 999px;
        }

        .ns-menu-card {
          position: relative;
          height: 100%;
          min-width: 0;
          border: 1px solid rgba(15, 23, 42, 0.10);
          border-radius: 16px;
          background: #ffffff;
          box-shadow: 0 8px 20px rgba(15, 23, 42, 0.055);
          padding: 10px 10px 9px 12px;
          cursor: pointer;
          display: grid;
          grid-template-rows: ${enableUnits ? "1fr 34px 32px" : "1fr 34px"};
          gap: ${enableUnits ? "6px" : "4px"};
          transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
          outline: none;
          overflow: hidden;
        }

        .ns-menu-card::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: #2563eb;
          opacity: 0.85;
          pointer-events: none;
        }

        .ns-menu-card:hover,
        .ns-menu-card:focus {
          border-color: rgba(37, 99, 235, 0.32);
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.10);
          transform: translateY(-1px);
        }

        .ns-menu-card:focus-visible {
          outline: 3px solid rgba(37, 99, 235, 0.18);
          outline-offset: 2px;
        }

        .ns-menu-card.is-added {
          border-color: rgba(22, 163, 74, 0.34);
          box-shadow: 0 14px 30px rgba(22, 163, 74, 0.10);
        }

        .ns-menu-card.is-added::before {
          background: #16a34a;
        }

        .ns-menu-card-head {
          position: relative;
          min-width: 0;
          display: grid;
          align-content: start;
          gap: 4px;
        }

        .ns-menu-name-line {
          min-width: 0;
          color: #0f172a;
          font-size: 0.86rem;
          font-weight: 950;
          line-height: 1.18;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          padding-right: 32px;
        }

        .ns-menu-meta-line {
          min-width: 0;
          color: #64748b;
          font-size: 0.68rem;
          font-weight: 820;
          line-height: 1.2;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          padding-right: 32px;
        }

        .ns-menu-added-chip {
          position: absolute;
          top: 0;
          right: 0;
          min-width: 27px;
          height: 24px;
          border-radius: 999px;
          background: #dcfce7;
          color: #047857;
          border: 1px solid #bbf7d0;
          font-size: 0.72rem;
          font-weight: 950;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 3px;
          padding: 0 7px;
        }

        .ns-menu-card-foot {
          position: relative;
          min-width: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 8px;
        }

        .ns-menu-price-wrap {
          min-width: 0;
          display: grid;
          gap: 1px;
        }

        .ns-menu-price-label {
          color: #94a3b8;
          font-size: 0.58rem;
          font-weight: 950;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          line-height: 1;
        }

        .ns-menu-price {
          color: #1d4ed8;
          font-size: 0.92rem;
          font-weight: 950;
          line-height: 1.1;
          white-space: nowrap;
        }

        .ns-menu-add-btn {
          width: 34px;
          height: 34px;
          border-radius: 13px;
          border: 0;
          background: #1d4ed8;
          color: #ffffff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          box-shadow: 0 10px 20px rgba(37, 99, 235, 0.20);
          transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
          flex: 0 0 auto;
        }

        .ns-menu-card.is-added .ns-menu-add-btn {
          background: #16a34a;
          box-shadow: 0 10px 20px rgba(22, 163, 74, 0.18);
        }

        .ns-menu-add-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 14px 26px rgba(37, 99, 235, 0.27);
          background: #0f172a;
        }

        .ns-menu-unit-row {
          min-width: 0;
          display: grid;
          grid-template-columns: minmax(0, 1fr) 28px;
          gap: 6px;
          align-items: center;
          padding-top: 5px;
          border-top: 1px dashed rgba(15, 23, 42, 0.10);
        }

        .ns-menu-unit-row.is-updating {
          opacity: 0.64;
          pointer-events: none;
        }

        .ns-menu-clear-unit {
          width: 28px;
          height: 28px;
          border-radius: 10px;
          border: 0;
          background: #fef2f2;
          color: #b91c1c;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.16s ease, transform 0.16s ease;
        }

        .ns-menu-clear-unit:hover {
          background: #fee2e2;
          transform: translateY(-1px);
        }

        .ns-menu-empty {
          grid-column: 1 / -1;
          min-height: 280px;
          border: 1px dashed rgba(100, 116, 139, 0.28);
          border-radius: 22px;
          background: #ffffff;
          display: flex;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 34px;
          color: #64748b;
        }

        .ns-menu-empty-icon {
          width: 58px;
          height: 58px;
          border-radius: 20px;
          background: #eff6ff;
          color: #1d4ed8;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
        }

        .ns-menu-empty-title {
          color: #0f172a;
          font-weight: 950;
          font-size: 1rem;
          margin-bottom: 5px;
        }

        .ns-menu-empty-text {
          font-size: 0.84rem;
          line-height: 1.5;
        }

        @media (min-width: 1500px) {
          .ns-menu-grid {
            grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
          }
        }

        @media (max-width: 1240px) {
          .ns-menu-grid {
            grid-template-columns: repeat(auto-fill, minmax(146px, 1fr));
            gap: 8px;
          }
        }

        @media (max-width: 768px) {
          .ns-menu-grid {
            grid-template-columns: repeat(auto-fill, minmax(142px, 1fr));
            grid-auto-rows: ${enableUnits ? "128px" : "88px"};
            gap: 8px;
            padding-bottom: 80px;
          }

          .ns-menu-name-line {
            font-size: 0.82rem;
          }

          .ns-menu-add-btn {
            width: 32px;
            height: 32px;
          }
        }
      `}</style>

      <div className="ns-menu-grid" role="list" aria-label="Menu items">
        {visibleItems.map((item) => {
          const menuId = getMenuId(item);
          const qty = getBillQty(billItems, menuId);
          const isAdded = qty > 0;
          const category = getItemCategory(item);
          const itemName = getOneLineName(item.name);
          const currentUnit =
            unitSelections[menuId] ||
            (item.unit ? { value: item.unit, label: item.unit } : { value: "NOS", label: "NOS" });
          const price = Number(item?.price_standard || item?.price || item?.rate || 0);
          const isUpdating = updatingUnitId && String(updatingUnitId) === String(menuId);

          return (
            <div
              key={menuId || item.name}
              className={`ns-menu-card ${isAdded ? "is-added" : ""}`}
              onClick={() => addItem(item)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  addItem(item);
                }
              }}
              title={itemName}
              aria-label={`Add ${itemName} to KOT`}
            >
              <div className="ns-menu-card-head">
                <div className="ns-menu-name-line">{itemName}</div>
                <div className="ns-menu-meta-line" title={category}>
                  {category}
                </div>

                {isAdded ? (
                  <span className="ns-menu-added-chip" aria-label={`${qty} quantity added`}>
                    <Check size={11} />
                    {Number(qty).toFixed(qty % 1 ? 2 : 0)}
                  </span>
                ) : null}
              </div>

              <div className="ns-menu-card-foot">
                <div className="ns-menu-price-wrap">
                  <div className="ns-menu-price-label">Price</div>
                  <div className="ns-menu-price">
                    {currency}
                    {money(price)}
                  </div>
                </div>

                <button
                  type="button"
                  className="ns-menu-add-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    addItem(item);
                  }}
                  aria-label={`Add ${itemName}`}
                >
                  <Plus size={16} />
                </button>
              </div>

              {enableUnits && (
                <div
                  className={`ns-menu-unit-row ${isUpdating ? "is-updating" : ""}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <Select
                    options={unitOptions}
                    value={currentUnit}
                    onChange={(unit) => handleUnitChange(menuId, unit)}
                    placeholder="Unit"
                    styles={unitSelectStyles}
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                  />

                  <button
                    type="button"
                    className="ns-menu-clear-unit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnitChange(menuId, null);
                    }}
                    aria-label={`Clear unit for ${itemName}`}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {visibleItems.length === 0 && (
          <div className="ns-menu-empty">
            <div>
              <div className="ns-menu-empty-icon">
                <Search size={28} />
              </div>
              <div className="ns-menu-empty-title">No items found</div>
              <div className="ns-menu-empty-text">
                Try another category or search term. Menu items will appear here for quick KOT selection.
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuItemGrid;
